<mat-toolbar class="content-header">
  <h1 class="page-title">
    <mat-icon class="header-icon" fontIcon="engineering"></mat-icon>
    {{ 'interventions.title' | translate }}
  </h1>
</mat-toolbar>

<ng-container>
  <div class="action-bar">
    <lib-button icon="filter_list" [color]="filtersOpen ? 'secondary' : 'outline'"
      text="{{ 'interventions.filters' | translate }}" (click)="toggleFilters()"
      data-testid="intervention-filters-toggle">
    </lib-button>

    <app-search-bar (searchText)="search($event)"></app-search-bar>

    <span class="action-bar-spacer"></span>

    <lib-button *ngIf="!adminMode" icon="add" (click)="openAdd()" color="primary"
      tooltip="{{ 'interventions.add' | translate }}" text="{{ 'interventions.add' | translate }}"
      data-testid="intervention-add-btn">
    </lib-button>
  </div>

  <div class="filters-panel" *ngIf="filtersOpen" data-testid="intervention-filters-panel">
    <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
      <mat-label>{{ 'interventions.field.category' | translate }}</mat-label>
      <mat-select [ngModel]="filters().category" (ngModelChange)="setCategoryFilter($event)">
        <mat-option value="">{{ 'form.all' | translate }}</mat-option>
        <mat-option *ngFor="let c of categories()" [value]="c">{{ c }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
      <mat-label>{{ 'interventions.field.subType' | translate }}</mat-label>
      <mat-select [ngModel]="filters().subType" (ngModelChange)="setSubTypeFilter($event)">
        <mat-option value="">{{ 'form.all' | translate }}</mat-option>
        <mat-option *ngFor="let s of subTypeFilterOptions()" [value]="s">{{ s }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field *ngIf="!adminMode" appearance="outline" class="filter-field" subscriptSizing="dynamic">
      <mat-label>{{ 'interventions.field.applicant' | translate }}</mat-label>
      <mat-select [ngModel]="filters().applicant" (ngModelChange)="setApplicantFilter($event)">
        <mat-option value="">{{ 'form.all' | translate }}</mat-option>
        <mat-option *ngFor="let u of users" [value]="u.id">{{ u.firstName }} {{ u.lastName }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field *ngIf="!adminMode" appearance="outline" class="filter-field" subscriptSizing="dynamic">
      <mat-label>{{ 'interventions.field.assignee' | translate }}</mat-label>
      <mat-select [ngModel]="filters().assignee" (ngModelChange)="setAssigneeFilter($event)">
        <mat-option value="">{{ 'form.all' | translate }}</mat-option>
        <mat-option *ngFor="let u of users" [value]="u.id">{{ u.firstName }} {{ u.lastName }}</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
      <mat-label>{{ 'interventions.filter.dateFrom' | translate }}</mat-label>
      <input matInput [matDatepicker]="fromPicker" [ngModel]="filters().dateFrom"
        (ngModelChange)="setDateFromFilter($event)">
      <mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
      <mat-datepicker #fromPicker></mat-datepicker>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
      <mat-label>{{ 'interventions.filter.dateTo' | translate }}</mat-label>
      <input matInput [matDatepicker]="toPicker" [ngModel]="filters().dateTo"
        (ngModelChange)="setDateToFilter($event)">
      <mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
      <mat-datepicker #toPicker></mat-datepicker>
    </mat-form-field>

    <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
      <mat-label>{{ 'interventions.filter.evaluationResult' | translate }}</mat-label>
      <mat-select [ngModel]="filters().evaluationResult" (ngModelChange)="setEvaluationResultFilter($event)">
        <mat-option *ngFor="let o of evaluationResultOptions" [value]="o.value">{{ o.label | translate }}</mat-option>
      </mat-select>
    </mat-form-field>

    <lib-button icon="filter_alt_off" color="none" textColor="secondary" tooltip="{{ 'form.reset' | translate }}"
      (click)="resetFilters()">
    </lib-button>
  </div>

  <div class="table-container">
    <div *ngIf="error()" class="error-banner">
      <mat-icon fontIcon="error"></mat-icon>
      <span>{{ 'interventions.load_error' | translate }}</span>
      <lib-button text="{{ 'form.retry' | translate }}" color="secondary" icon="refresh" (click)="retry()">
      </lib-button>
    </div>

    <div class="table-wrapper">
      <table mat-table [dataSource]="interventionsData" matSort matSortActive="dueDate" matSortDirection="desc"
        data-testid="intervention-table">
        <ng-container matColumnDef="project">
          <th mat-header-cell *matHeaderCellDef scope="col">
            <div class="header-content">
              <mat-icon fontIcon="folder"></mat-icon>
              {{ 'interventions.field.project' | translate }}
            </div>
          </th>
          <td mat-cell *matCellDef="let row">
            {{ row.projectName || '—' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="dueDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">
            <div class="header-content">
              <mat-icon fontIcon="event"></mat-icon>
              {{ 'interventions.field.dueDate' | translate }}
            </div>
          </th>
          <td mat-cell *matCellDef="let row">
            {{ row.dueDate ? (row.dueDate | timeFormat: 'dd/MM/yyyy') : '—' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="category">
          <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">
            <div class="header-content">
              <mat-icon fontIcon="category"></mat-icon>
              {{ 'interventions.field.category' | translate }}
            </div>
          </th>
          <td mat-cell *matCellDef="let row">
            {{ row.category }}
          </td>
        </ng-container>

        <ng-container matColumnDef="subType">
          <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">
            <div class="header-content">
              <mat-icon fontIcon="label"></mat-icon>
              {{ 'interventions.field.subType' | translate }}
            </div>
          </th>
          <td mat-cell *matCellDef="let row">
            {{ row.subType }}
          </td>
        </ng-container>

        <ng-container matColumnDef="description">
          <th mat-header-cell *matHeaderCellDef scope="col">
            <div class="header-content">
              <mat-icon fontIcon="description"></mat-icon>
              {{ 'interventions.field.description' | translate }}
            </div>
          </th>
          <td mat-cell *matCellDef="let row" class="col-description">
            {{ descriptionPreview(row.description) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="assignee">
          <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">
            <div class="header-content">
              <mat-icon fontIcon="assignment_ind"></mat-icon>
              {{ 'interventions.field.assignee' | translate }}
            </div>
          </th>
          <td mat-cell *matCellDef="let row">
            {{ adminMode ? assigneeLabel(row) : userLabel(row.assignee) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="evaluationResult">
          <th mat-header-cell *matHeaderCellDef mat-sort-header scope="col">
            <div class="header-content">
              <mat-icon fontIcon="fact_check"></mat-icon>
              {{ 'interventions.field.evaluationResult' | translate }}
            </div>
          </th>
          <td mat-cell *matCellDef="let row">
            <lib-chip [color]="evaluationChipColor(row.evaluation?.result)"
              [hexColor]="evaluationChipHexColor(row.evaluation?.result)"
              [style.color]="'white'"
              [text]="evaluationLabelKey(row.evaluation?.result) | translate">
            </lib-chip>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="interventionsColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: interventionsColumns;" class="clickable" tabindex="0"
          role="button"
          [attr.aria-label]="'interventions.row_aria_label' | translate: { category: row.category, subType: row.subType, dueDate: row.dueDate ? (row.dueDate | timeFormat: 'dd/MM/yyyy') : '—', evaluation: (evaluationLabelKey(row.evaluation?.result) | translate) }"
          (click)="openEdit(row)" (keydown)="onRowKeydown($event, row)" data-testid="intervention-row"></tr>

        <tr class="mat-row no-data-row" *matNoDataRow>
          <td class="mat-cell" [attr.colspan]="interventionsColumns.length">
            <lib-no-data message="interventions.empty" description="interventions.empty_desc" icon="engineering">
              <lib-button *ngIf="!adminMode" icon="add" color="primary" text="{{ 'interventions.add' | translate }}"
                (click)="openAdd()">
              </lib-button>
            </lib-no-data>
          </td>
        </tr>
      </table>
    </div>

    <div class="card-list" data-testid="intervention-card-list">
      <mat-card *ngFor="let row of visibleRows" class="intervention-card clickable" tabindex="0" role="button"
        [attr.aria-label]="'interventions.row_aria_label' | translate: { category: row.category, subType: row.subType, dueDate: row.dueDate ? (row.dueDate | timeFormat: 'dd/MM/yyyy') : '—', evaluation: (evaluationLabelKey(row.evaluation?.result) | translate) }"
        (click)="openEdit(row)" (keydown)="onRowKeydown($event, row)" data-testid="intervention-card">
        <mat-card-content>
          <div class="card-top">
            <div class="card-heading">
              <span class="card-category">{{ row.category }}</span>
              <span class="card-subtype">{{ row.subType }}</span>
            </div>
            <lib-chip [color]="evaluationChipColor(row.evaluation?.result)"
              [hexColor]="evaluationChipHexColor(row.evaluation?.result)"
              [style.color]="'white'"
              [text]="evaluationLabelKey(row.evaluation?.result) | translate">
            </lib-chip>
          </div>

          <div class="card-rows">
            <div class="card-row" *ngIf="adminMode">
              <mat-icon fontIcon="folder"></mat-icon>
              <span class="card-row-label">{{ 'interventions.field.project' | translate }}</span>
              <span class="card-row-value">{{ row.projectName || '—' }}</span>
            </div>

            <div class="card-row">
              <mat-icon fontIcon="event"></mat-icon>
              <span class="card-row-label">{{ 'interventions.field.dueDate' | translate }}</span>
              <span class="card-row-value">{{ row.dueDate ? (row.dueDate | timeFormat: 'dd/MM/yyyy') : '—' }}</span>
            </div>

            <div class="card-row card-row-description">
              <div class="card-row-heading">
                <mat-icon fontIcon="description"></mat-icon>
                <span class="card-row-label">{{ 'interventions.field.description' | translate }}</span>
              </div>
              <span class="card-row-value">{{ descriptionPreview(row.description) }}</span>
            </div>

            <div class="card-row">
              <mat-icon fontIcon="assignment_ind"></mat-icon>
              <span class="card-row-label">{{ 'interventions.field.assignee' | translate }}</span>
              <span class="card-row-value">{{ adminMode ? assigneeLabel(row) : userLabel(row.assignee) }}</span>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <lib-no-data *ngIf="!loading() && visibleRows.length === 0" message="interventions.empty"
        description="interventions.empty_desc" icon="engineering">
        <lib-button *ngIf="!adminMode" icon="add" color="primary" text="{{ 'interventions.add' | translate }}"
          (click)="openAdd()">
        </lib-button>
      </lib-no-data>
    </div>

    <div class="pagination-footer">
      <mat-paginator #interventionPaginator [length]="count()" [pageSize]="20" [pageSizeOptions]="[20, 50, 100]"
        (page)="onPageChange($event)" class="paginator">
      </mat-paginator>
      <span class="pagination-count">{{ 'interventions.count' | translate:{ count: count() } }}</span>
    </div>

    <div *ngIf="loading() || loadingDetail" class="table-overlay">
      <app-spinner></app-spinner>
    </div>
  </div>
</ng-container>

<app-intervention-drawer *ngIf="drawerMounted" [open]="drawerOpen" [disableAnimation]="drawerAnimationDisabled"
  [intervention]="selectedIntervention" [mode]="drawerMode" [adminMode]="adminMode"
  [projectId]="adminMode ? (selectedIntervention?.project ?? '') : project?.id"
  [presetTracker]="presetTrackerId" (closed)="onDrawerClosed()" (saved)="onDrawerSaved($event)"
  (deleted)="onDrawerDeleted($event)">
</app-intervention-drawer>
